import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

/**
 * FormAjaxSelect
 *
 * A reusable, async-capable dropdown select component (react-select style):
 * - Displays a clickable select control, not a free text input.
 * - Shows a dropdown with a search box and async-loaded options.
 * - Clearable (optional "x" button to reset the value).
 *
 * 1) Preferred: provide async loadOptions(query) and let this component own
 *    options/loading state internally.
 * 2) Legacy: provide onSearch(query) + options + isLoading, where parent owns
 *    Redux/async state. For new code, prefer loadOptions.
 */
const FormAjaxSelect = ({
	name,
	control,
	label,
	// Static options always shown (e.g. defaults)
	staticOptions = [],
	// Async loader: (query) => Promise<option[]>
	loadOptions,
	callbackFunc,
	options: controlledOptions,
	onSearch,
	isLoading: controlledIsLoading = false,
	placeholder = 'Select...',
	required = false,
	disabled = false,
	className = '',
	helpText,
	validate,
	rules = {},
	optionValueKey = 'value',
	optionLabelKey = 'label',
	// Optional accessors to derive value/label dynamically from items
	valueAccessor,
	labelAccessor,
	minChars = 2,
	debounceMs = 300,
	maxHeight = '240px',
	noResultsText = 'No results found',
	loadingText = 'Searching...',
	clearable = true,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [asyncOptions, setAsyncOptions] = useState([]);
	const [localIsLoading, setLocalIsLoading] = useState(false);
	const containerRef = useRef(null);
	const debounceRef = useRef(null);
	const dispatch = useDispatch();

	// Build validation rules similar to FormSelect
	const validationRules = {
		required: required
			? typeof required === 'string'
				? required
				: `${label || 'Field'} is required`
			: false,
		...(validate && { validate }),
		...rules,
	};

	// Shared async loader used by both debounced search and initial load
	const runAsyncLoad = useCallback(
		async (query, withoutQuery = false) => {
			if (!loadOptions && !onSearch && !callbackFunc) return;

			if (query.length < minChars && !withoutQuery) {
				return;
			}

			// 1) callbackFunc: usually a thunk factory like (q) => adminUsersThunks.fetchPaginated(...)
			if (callbackFunc) {
				try {
					setLocalIsLoading(true);
					const response = await dispatch(callbackFunc(query));
					const result = response?.data || response || [];
					setAsyncOptions(Array.isArray(result) ? result : []);
				} catch (e) {
					setAsyncOptions([]);
				} finally {
					setLocalIsLoading(false);
				}
				return;
			}

			// 2) loadOptions: (query) => Promise<items[]>
			if (loadOptions) {
				try {
					setLocalIsLoading(true);
					const result = await loadOptions(query);
					setAsyncOptions(Array.isArray(result) ? result : []);
				} catch (e) {
					setAsyncOptions([]);
				} finally {
					setLocalIsLoading(false);
				}
				return;
			}

			// 3) Legacy: delegate to parent and expect it to update options prop
			if (onSearch) {
				onSearch(query);
			}
		},
		[loadOptions, onSearch, callbackFunc, minChars, dispatch]
	);

	// Debounced search
	const triggerSearch = (query) => {
		if (!loadOptions && !onSearch && !callbackFunc) return;

		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		debounceRef.current = setTimeout(() => {
			if (query.length < minChars) return;
			runAsyncLoad(query);
		}, debounceMs);
	};

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchTerm(value);
		triggerSearch(value);
	};

	const handleClear = (e, field) => {
		e.stopPropagation();
		field.onChange('');
		setSearchTerm('');
	};

	const getOptionValue = (option, index) => {
		if (typeof valueAccessor === 'function') {
			return valueAccessor(option, index);
		}
		if (typeof option === 'string' || typeof option === 'number') return option;
		if (option && typeof option === 'object') {
			return option[optionValueKey] ?? option.value ?? option.id;
		}
		return option;
	};

	const getOptionLabel = (option, index) => {
		if (typeof labelAccessor === 'function') {
			return labelAccessor(option, index);
		}
		if (typeof option === 'string' || typeof option === 'number') return option;
		if (option && typeof option === 'object') {
			return (
				option[optionLabelKey] ||
				option.label ||
				option.name ||
				option.text ||
				''
			);
		}
		return '';
	};

	// Close dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Clear debounce on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!loadOptions && !callbackFunc) return;
		runAsyncLoad('', true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Decide where options/loading come from
	const mergedOptions =
		loadOptions || callbackFunc
			? [...staticOptions, ...asyncOptions]
			: controlledOptions || staticOptions;
	const effectiveIsLoading =
		loadOptions || callbackFunc ? localIsLoading : controlledIsLoading;

	return (
		<Controller
			name={name}
			control={control}
			rules={validationRules}
			render={({ field, fieldState: { error } }) => {
				const selectedIndex = mergedOptions.findIndex(
					(opt, idx) => getOptionValue(opt, idx) === field.value
				);
				const selectedOption =
					selectedIndex >= 0 ? mergedOptions[selectedIndex] : null;
				const selectedLabel = selectedOption
					? getOptionLabel(selectedOption, selectedIndex)
					: '';

				const onToggleOpen = () => {
					if (disabled) return;
					const next = !isOpen;
					if (next) {
						// When opening, initialize search text with current label for easier editing,
						// but DO NOT trigger a new async search automatically.
						setSearchTerm(selectedLabel || '');
					}
					setIsOpen(next);
				};

				const controlValue = isOpen ? searchTerm : selectedLabel || '';
				const controlPlaceholder = selectedLabel ? '' : placeholder;

				return (
					<div
						className={`mb-3 ${className}`}
						ref={containerRef}
						style={{ position: 'relative' }}
					>
						{label && (
							<label htmlFor={name} className="form-label">
								{label} {required && <span className="text-danger">*</span>}
							</label>
						)}

						{/* Select control (with inline search input) */}
						<button
							type="button"
							id={name}
							className={`form-select text-start d-flex justify-content-between align-items-center ${
								error ? 'is-invalid' : ''
							}`}
							onClick={onToggleOpen}
							disabled={disabled}
						>
							<input
								type="text"
								className="border-0 bg-transparent flex-grow-1 form-control-plaintext p-0"
								placeholder={controlPlaceholder}
								value={controlValue}
								onChange={handleSearchChange}
								onClick={(e) => {
									// prevent bubbling to button so we don't immediately toggle twice
									e.stopPropagation();
									if (!isOpen) {
										onToggleOpen();
									}
								}}
								disabled={disabled}
							/>
							<span className="d-flex align-items-center">
								{clearable && field.value && !disabled && (
									<button
										type="button"
										className="btn btn-sm btn-link p-0 me-1 text-muted"
										onClick={(e) => handleClear(e, field)}
										aria-label="Clear selection"
									>
										<i className="bi bi-x" />
									</button>
								)}
								{/* <i className="bi bi-chevron-down" /> */}
							</span>
						</button>

						{/* Dropdown */}
						{isOpen && (
							<div
								className="dropdown-menu show w-100"
								style={{
									position: 'absolute',
									top: '100%',
									left: 0,
									zIndex: 1050,
									maxHeight,
									overflowY: 'auto',
								}}
							>
								{/* Options / loading / empty states */}
								{effectiveIsLoading ? (
									<div className="dropdown-item-text d-flex align-items-center">
										<div
											className="spinner-border spinner-border-sm me-2"
											role="status"
										>
											<span className="visually-hidden">Loading...</span>
										</div>
										{loadingText}
									</div>
								) : mergedOptions.length > 0 ? (
									mergedOptions.map((option, index) => {
										const value = getOptionValue(option, index);
										const labelText = getOptionLabel(option, index);
										const isSelected = value === field.value;

										return (
											<button
												key={`${name}-opt-${index}`}
												type="button"
												className={`dropdown-item ${
													isSelected ? 'active' : ''
												}`}
												onClick={() => {
													field.onChange(value);
													setIsOpen(false);
												}}
											>
												{labelText}
											</button>
										);
									})
								) : (
									<div className="dropdown-item-text text-muted small">
										{noResultsText}
									</div>
								)}
							</div>
						)}

						{error && (
							<div className="invalid-feedback d-block">{error.message}</div>
						)}
						{helpText && !error && <div className="form-text">{helpText}</div>}
					</div>
				);
			}}
		/>
	);
};

FormAjaxSelect.propTypes = {
	name: PropTypes.string.isRequired,
	control: PropTypes.object.isRequired,
	label: PropTypes.string,
	staticOptions: PropTypes.array,
	loadOptions: PropTypes.func,
	options: PropTypes.array,
	onSearch: PropTypes.func,
	isLoading: PropTypes.bool,
	placeholder: PropTypes.string,
	required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	disabled: PropTypes.bool,
	className: PropTypes.string,
	helpText: PropTypes.string,
	validate: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	rules: PropTypes.object,
	optionValueKey: PropTypes.string,
	optionLabelKey: PropTypes.string,
	valueAccessor: PropTypes.func,
	labelAccessor: PropTypes.func,
	minChars: PropTypes.number,
	debounceMs: PropTypes.number,
	maxHeight: PropTypes.string,
	noResultsText: PropTypes.string,
	loadingText: PropTypes.string,
	clearable: PropTypes.bool,
};

export default FormAjaxSelect;
